import { useCallback, useRef } from 'react';
import { log } from 'log';
import getValidateFn from 'js/utils/Validation';
import { useTranslations } from 'js/hooks/i18n';
import { useGetErrorMessage } from 'js/hooks/server';
import { useCheckUsernameAvailability } from 'server';
import { ICheckUsernameAvailabilityResponseData } from 'js/hooks/socket/hooks/useCheckUsernameAvailability/types';

import * as Types from './types';
import { INTERNAL_EMAIL } from '../../../../constants';

export const useValidateEmail = ({
    fieldName,
    validationConfig,
    requestReCaptchaToken,
    abortReCaptchaTokenRequest
}: Types.IUseValidateEmailOptions) => {
    const translate = useTranslations();
    const getErrorMessage = useGetErrorMessage();
    const checkIfEmailIsAvailable = useCheckUsernameAvailability();
    const cachedEmail = useRef<string | null>(null);
    const abortController = useRef<AbortController | null>(null);
    const cachedRecaptchaValidationRequest = useRef<Promise<{ token: string }> | null>(null);
    const cachedServerResponse = useRef<ICheckUsernameAvailabilityResponseData | null>(null);

    const getRecordLevelValidation = useCallback((email: string): Promise<Record<string, string> | null> => {
        const recordLevelValidate = getValidateFn({ validationConfig: { [fieldName]: validationConfig } });

        return recordLevelValidate({ [fieldName]: email });
    }, [fieldName, validationConfig]);

    // this has to be async function so that React Final Form will wait for resolution
    const asyncServerRequest = useCallback(async function process(email: string, validationToken?: string):
        Promise<Types.IUseValidateEmailResponse> {
        try {
            const response = await checkIfEmailIsAvailable({
                email,
                ...validationToken && { token: validationToken }
            });

            const { emailAvailable } = response;

            cachedEmail.current = email;
            cachedServerResponse.current = response;
            cachedRecaptchaValidationRequest.current = null;
            abortController.current?.abort();

            if (emailAvailable !== 'available') {
                return translate('playtech.mobile.system.form.email.internal.emailIsInUse');
            }
        } catch (error: Types.TCheckIfEmailIsAvailableErrorResponse) {
            const { data: { errorCode } } = error.response;

            if (errorCode === 301) {
                try {
                    abortController.current = new AbortController();
                    abortController.current?.signal.addEventListener('abort', abortReCaptchaTokenRequest);

                    const tokenResponse = requestReCaptchaToken();

                    cachedRecaptchaValidationRequest.current = tokenResponse;

                    const { token } = await tokenResponse;

                    return await process(email, token);
                } catch (validationError) {
                    log('error', validationError);

                    return getErrorMessage(validationError);
                }
            }

            return getErrorMessage(error);
        }
    }, [
        translate,
        getErrorMessage,
        requestReCaptchaToken,
        checkIfEmailIsAvailable,
        abortReCaptchaTokenRequest
    ]);

    // this has to be async function so that React Final Form will wait for resolution
    const asyncCachedRequest = useCallback(
        async (email: string, recaptchaValidationPromise: Promise<{ token: string }>) => {
            let recaptchaToken;

            try {
                const data = await recaptchaValidationPromise;

                recaptchaToken = data.token;
            } catch {
                return await asyncServerRequest(email);
            }

            return await asyncServerRequest(email, recaptchaToken);
        }, [asyncServerRequest]);

    // this has to be sync function so that there is no jumping during re-renders
    return useCallback(async (email: string): Promise<Types.IUseValidateEmailResponse> => {
        // 'Field level' validation should be run after 'Record level' one
        const recordLevelValidation = await getRecordLevelValidation(email);

        if (!email || recordLevelValidation || email === INTERNAL_EMAIL) {
            return;
        }

        if (cachedEmail.current === email) {
            const { emailAvailable } = cachedServerResponse.current ?? {};

            if (emailAvailable === 'available') {
                return;
            }

            return translate('playtech.mobile.system.form.email.internal.emailIsInUse');
        }

        if (cachedRecaptchaValidationRequest.current) {
            return asyncCachedRequest(email, cachedRecaptchaValidationRequest.current);
        }

        return asyncServerRequest(email);
    }, [
        translate,
        asyncServerRequest,
        asyncCachedRequest,
        getRecordLevelValidation
    ]);
};
